import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { CONTAINER } from "./ui-tokens";

/**
 * Título de seção da landing.
 *
 * Duas frases em dois tons: a primeira em cor de texto, a segunda em
 * `muted-foreground`. É o contraste de peso que carrega a hierarquia — a
 * versão anterior fazia isso com a segunda frase em itálico azul, repetida em
 * todas as treze seções, e o azul (que no sistema quer dizer "clicável")
 * passava a marcar texto estático.
 *
 * Sem sobretítulo em caixa-alta: ele aparecia em todas as seções com o mesmo
 * corpo, e o que se repete em todo lugar deixa de orientar.
 */
export function SectionHeading({
  id,
  title,
  muted,
  children,
  align = "start",
  className,
}: {
  /** id do h2, para `aria-labelledby` da seção. */
  id?: string;
  title: ReactNode;
  muted?: ReactNode;
  /** Parágrafo de apoio, opcional. */
  children?: ReactNode;
  align?: "start" | "center";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "lp-reveal max-w-2xl",
        align === "center" && "mx-auto text-center",
        className,
      )}
    >
      <h2
        id={id}
        className="text-3xl font-semibold leading-[1.1] tracking-tight text-balance text-foreground md:text-[2.625rem]"
      >
        {title}
        {muted ? (
          <>
            {" "}
            <span className="text-muted-foreground">{muted}</span>
          </>
        ) : null}
      </h2>
      {children ? (
        <p className="mt-5 text-base leading-relaxed text-pretty text-muted-foreground md:text-lg">
          {children}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A faixa escura de fechamento das páginas internas do site (comparativos,
 * páginas de módulo): título, uma frase, as ações e a nota miúda embaixo.
 * A ação principal é `buttonClass("inverse")`; a secundária, `ghost-inverse`.
 */
export function ClosingBand({
  title,
  children,
  actions,
  note,
}: {
  title: string;
  children: ReactNode;
  actions: ReactNode;
  note?: ReactNode;
}) {
  return (
    <section className="bg-lp-band text-lp-band-foreground">
      <div className={`${CONTAINER} py-16 md:py-20`}>
        <div className="max-w-2xl">
          <h2 className="text-2xl font-semibold leading-tight tracking-tight text-balance md:text-4xl">
            {title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-pretty text-lp-band-foreground/75">
            {children}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">{actions}</div>
          {note && <p className="mt-4 text-sm text-lp-band-foreground/65">{note}</p>}
        </div>
      </div>
    </section>
  );
}

/** Primeiro foco da página: pula o cabeçalho e vai direto ao `<main>`. */
export function SkipLink({ target = "conteudo" }: { target?: string }) {
  return (
    <a
      href={`#${target}`}
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-60 focus:rounded-full focus:bg-primary focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-primary-foreground"
    >
      Pular para o conteúdo
    </a>
  );
}
